import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ReversedLegend } from './ReversedLegend.tsx';
import { BAR_COLOR, LINE_COLOR } from './consts.ts';
import { SiteSizeVsCostPerSFChartData, SiteSizeVsSiteValueChartData } from './types.ts';

type Props = {
  data: SiteSizeVsCostPerSFChartData[] | SiteSizeVsSiteValueChartData[];
  comparisonKey: string;
  name: string;
  tickFormatter: (val: number) => string;
  domain?: ([dataMin, dataMax]: [number, number]) => [number, number];
  label?: string;
  tooltipFormatter: (val: number) => string;
  disableAnimations?: boolean;
  width?: number;
  height?: number;
};

const Chart = ({
  comparisonKey,
  data,
  name,
  tickFormatter,
  tooltipFormatter,
  disableAnimations,
  domain,
  label,
  width,
  height,
}: Props) => (
  <ComposedChart data={data} width={width} height={height}>
    <CartesianGrid vertical={false} />
    <Legend content={<ReversedLegend />} />
    {!disableAnimations && (
      <Tooltip
        formatter={(val, chartName) => {
          if (chartName === 'Site Size') return `${val.toLocaleString()} SF`;
          if (tooltipFormatter) return tooltipFormatter(val as number);

          return val;
        }}
      />
    )}
    <YAxis
      yAxisId="siteSize"
      orientation="left"
      tickFormatter={(val: string) => val.toLocaleString()}
      width={80} // to accommodate the label
    >
      <Label angle={-90} position="left" offset={-5}>
        Site Size (SF)
      </Label>
    </YAxis>
    <YAxis
      yAxisId={comparisonKey}
      orientation="right"
      tickFormatter={tickFormatter}
      domain={domain}
      width={80} // to accommodate the label
    >
      <Label angle={90} position="right" offset={-5}>
        {label ?? name}
      </Label>
    </YAxis>
    <XAxis dataKey="siteName" height={60} />
    <Bar
      name={name}
      dataKey={comparisonKey}
      fill={BAR_COLOR}
      yAxisId={comparisonKey}
      isAnimationActive={!disableAnimations}
    />
    <Line
      name="Site Size"
      dataKey="siteSize"
      yAxisId="siteSize"
      stroke={LINE_COLOR}
      isAnimationActive={!disableAnimations}
    />
  </ComposedChart>
);

export const SiteSizeComparisonChart = (props: Props) => {
  const { width, height, ...chartProps } = props;

  if (width && height) return <Chart {...chartProps} width={width} height={height} />;

  return (
    <ResponsiveContainer width="100%" height={350}>
      <Chart {...chartProps} />
    </ResponsiveContainer>
  );
};
